import { If } from 'common/src/components';
import { findDOMNode } from 'react-dom';
import { $document } from '@umu/umu-dom';
import { Iconfont } from '@umu/umu-design';
import { commonLang } from '@umu/ukit-lang';
import useDebounceFn from 'ahooks/es/useDebounceFn';
import './searchInputForContentBaseOfUseNormal.scss';
import { transformPx2Rem } from 'businessCommon/src/@utils';
import { IAppRecommendData, IAppSearchData } from '@umu/umu-types';
import React, { createRef, useState, useEffect, useRef, RefObject } from 'react';
import { defaultRecommendData } from '@umu/umu-search/src/searchInputForContent/common/dataflow';
import FloatingLayerForContentBase from '@umu/umu-search/src/searchInputForContent/common/components/floatingLayerForContentBase/floatingLayerForContentBase';

export interface SearchInputForContentBaseOfUseNormalProps {
    isHiddenSearchIcon?: boolean;
    placeholder?: string;
    fontSize?: number;
    disabled?: boolean;
    searchKeyword?: string;
    showFloatingLayer: boolean;
    defaultFloatValue: boolean;
    recommendData: IAppRecommendData;
    searchCallback?(keywords: string): void;
    cleanCallback(): void;
    searchInputOnFocus?(): void;
    clickCallback?(data: IAppSearchData, key: string): void;
    onChange?(keywords: string): void;
    onClearBtnClick?(): void;
    searchInputCallback?(keywords: string): void;
    searchInputClearCallback?: () => void;
}

const SearchInputForContentBaseOfNormalCommon = (props: SearchInputForContentBaseOfUseNormalProps) => {
    const placeholder = props.placeholder || commonLang('搜索');
    const fontSize = props.fontSize || 14;
    const elementRef: RefObject<HTMLDivElement> = useRef(null);
    const recommendData = props.recommendData || defaultRecommendData;
    const { historySearch, popular, historyCourse, LenovoCourse } = recommendData;
    const [keywords, setKeywords] = useState(props.searchKeyword || '');
    const [panelType, setPanelType] = useState('history');
    const isKeyword = props.searchKeyword && props.searchKeyword !== '';
    const floatTypes = isKeyword ? false : props.showFloatingLayer ? props.defaultFloatValue : false;
    const [showFloatingLayer, setShowFloatingLayer] = useState(floatTypes);
    const [showMouseDown, setShowMouseDown] = useState(false);
    const searchInputRef: RefObject<HTMLDivElement> = createRef();
    const searchInputKeywordRef = useRef<HTMLInputElement>(null);
    const [isCancel, setIsCancel] = useState(!!(keywords && keywords !== ''));
    const isCompositing = useRef<boolean>(false); // 是否正在输入拼写

    const setParentCallback = (value: string) => {
        if (props.searchCallback) {
            props.searchCallback(value);
        }
    };

    const setCancelKeyWord = () => {
        setKeywords('');
        setIsCancel(false);
        props?.searchInputClearCallback?.();
    };

    const getRefInputValue = () => {
        const value = searchInputKeywordRef.current ? searchInputKeywordRef.current.value : '';
        return value;
    };

    const onChangePropDebounceWrap = useDebounceFn(props?.onChange || function () {}, {
        wait: 200,
        leading: false,
        trailing: true,
    });

    const onChange = () => {
        const value = getRefInputValue();
        setIsCancel(value !== '');
        setKeywords(value);
        onChangePropDebounceWrap?.run(value);
    };

    const getSearchCourse = () => {
        const value = getRefInputValue();
        if (props.searchInputCallback) {
            props.searchInputCallback(value);
        }
    };

    const getKeyWorDuse = useDebounceFn(getSearchCourse, {
        wait: 500,
        leading: true,
        trailing: true,
    });

    const setSearchCourse = () => {
        const value = getRefInputValue();

        setKeywords(value);
        const types = value === '' ? 'history' : 'lenovo';
        if (props.showFloatingLayer) {
            getKeyWorDuse.run();
            setShowFloatingLayer(true);
            setPanelType(types);
        }
        return value;
    };

    const keyWordHook = useDebounceFn(setSearchCourse, {
        wait: 500,
        leading: false,
        trailing: true,
    });

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (isCompositing.current) {
            return;
        }
        switch (e.keyCode) {
            case 13:
                setParentCallback(keywords);
                setShowFloatingLayer(false);
                break;
            default:
                panelChange();
                break;
        }
        keyWordHook.run();
    };

    const panelChange = () => {
        const value = getRefInputValue();
        const types = value === '' ? 'history' : 'lenovo';
        if (props.showFloatingLayer) {
            setShowFloatingLayer(true);
        }
        setPanelType(types);
        setIsCancel(value !== '');
    };

    useEffect(() => {
        if (showFloatingLayer) {
            $document.addEventListener('mousedown', (e) => handleClick(e), false);
        }
        return () => {
            $document.removeEventListener('mousedown', (e) => handleClick(e), false);
        };
    }, [showFloatingLayer]);

    const handleClick = (event: Event) => {
        if (elementRef && elementRef.current) {
            const target = event.target as HTMLElement;
            const divDom = elementRef.current && findDOMNode(elementRef.current);
            const res = divDom && divDom.contains(target);
            if (res) {
                panelChange();
                keyWordHook.run();
            }
            clickCallback({ mousedown: !!res, eventData: event }, 'mousedown');
        }
    };

    const searchInputOnFocus = () => {
        if (keywords) {
            setIsCancel(true);
        }

        if (props.showFloatingLayer) {
            setShowFloatingLayer(true);
            clickCallback({ mousedown: true }, 'mousedown');
            getSearchCourse();
            if (keywords && keywords !== '') {
                setPanelType('lenovo');
            } else {
                setPanelType('history');
            }
        }
        if (props.searchInputOnFocus) {
            props.searchInputOnFocus();
        }
    };

    const searchInputOnBlur = () => {
        setShowFloatingLayer(showMouseDown);
        setTimeout(() => {
            setIsCancel(false);
        }, 500);
    };

    const clickCallback = (data: IAppSearchData, key: string) => {
        if (key === 'mousedown') {
            setShowMouseDown(!!data.mousedown);
            setShowFloatingLayer(!!data.mousedown);
            // return;
        }
        if (props.clickCallback && props.showFloatingLayer) {
            props.clickCallback(data, key);
        }
    };

    const getFloatShowOrHide = (type: string) => {
        if (type === 'history') {
            let shows = historySearch && historySearch.length > 0;
            shows = shows || (historyCourse && historyCourse.length > 0);
            shows = shows || (popular && popular.length > 0);
            return showFloatingLayer && shows;
        } else {
            return showFloatingLayer && LenovoCourse && LenovoCourse.length > 0;
        }
    };

    const handleComposition = (e: React.CompositionEvent<HTMLInputElement>) => {
        if (e.type === 'compositionend') {
            isCompositing.current = false;
            return;
        }
        isCompositing.current = true;
    };

    return (
        <div className="recommend-search-input search-input-for-content-base__use-normal" ref={searchInputRef}>
            <div className="search-input">
                <div className="input-box" ref={elementRef}>
                    <div className="input-box-search-icon">
                        <Iconfont type="search"></Iconfont>
                    </div>
                    <input
                        type="search"
                        disabled={props.disabled}
                        ref={searchInputKeywordRef}
                        onFocus={searchInputOnFocus}
                        onBlur={searchInputOnBlur}
                        onCompositionStart={handleComposition}
                        onCompositionUpdate={handleComposition}
                        onCompositionEnd={handleComposition}
                        className={`recommend-input-text search-input-keyword recommend-input`}
                        placeholder={placeholder}
                        style={{ fontSize: transformPx2Rem(`${fontSize}px`) }}
                        maxLength={100}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        value={keywords}
                    />

                    <If condition={isCancel}>
                        <div className={`do-search-cancel`} onClick={() => setCancelKeyWord()} />
                    </If>

                    <If condition={getFloatShowOrHide(panelType)}>
                        <FloatingLayerForContentBase
                            inputType="useCommon"
                            className="search-panel"
                            panelType="pc"
                            historySearch={historySearch}
                            cleanCallback={props.cleanCallback}
                            clickCallback={clickCallback}
                            popular={popular}
                            LenovoCourse={LenovoCourse}
                            historyCourse={historyCourse}
                            showType={panelType}
                        />
                    </If>
                </div>
            </div>
        </div>
    );
};

export default SearchInputForContentBaseOfNormalCommon;
